import { useState, useEffect } from 'react';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

const TABS = ['manual', 'guided'];

export default function StripeSettings({ setPage }) {
  const [tab, setTab] = useState('manual');
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({ stripe_secret_key: '', stripe_publishable_key: '', stripe_webhook_secret: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [msg, setMsg] = useState('');

  useEffect(() => { loadSettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await api.get('/stripe/settings');
      setSettings(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const payload = {};
      if (form.stripe_secret_key) payload.stripe_secret_key = form.stripe_secret_key;
      if (form.stripe_publishable_key) payload.stripe_publishable_key = form.stripe_publishable_key;
      if (form.stripe_webhook_secret) payload.stripe_webhook_secret = form.stripe_webhook_secret;
      payload.stripe_enabled = true;
      await api.put('/stripe/settings', payload);
      setMsg('✅ Сохранено');
      setForm({ stripe_secret_key: '', stripe_publishable_key: '', stripe_webhook_secret: '' });
      loadSettings();
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || 'Ошибка'));
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post('/stripe/test');
      setTestResult({ ok: true, ...res.data });
    } catch (e) {
      setTestResult({ ok: false, error: e.response?.data?.detail || 'Ошибка подключения' });
    } finally {
      setTesting(false);
    }
  };

  const toggleAutoInvoice = async (field, value) => {
    try {
      await api.put('/stripe/settings', { [field]: value });
      loadSettings();
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <div style={{ padding: 20, textAlign: 'center' }}>{t('common.loading')}</div>;

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)',
    color: '#fff', fontSize: 13, fontFamily: 'monospace', boxSizing: 'border-box',
  };
  const btnStyle = (color = '#6366f1') => ({
    padding: '10px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
    background: color, color: '#fff', fontWeight: 600, fontSize: 13,
  });
  const labelStyle = { fontSize: 12, opacity: 0.6, marginBottom: 4, display: 'block' };

  return (
    <div style={{ padding: 16, maxWidth: 600 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <span onClick={() => setPage?.('dashboard_owner')} style={{ cursor: 'pointer', fontSize: 20 }}>←</span>
        <h2 style={{ margin: 0, fontSize: 20 }}>💳 Stripe Integration</h2>
      </div>

      {/* Status */}
      <div style={{
        padding: 16, borderRadius: 12, marginBottom: 16,
        background: settings?.connected ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
        border: `1px solid ${settings?.connected ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
      }}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
          {settings?.connected ? '✅ Подключено к Stripe' : '❌ Stripe не подключен'}
        </div>
        {settings?.connected && (
          <div style={{ fontSize: 12, opacity: 0.7 }}>
            <div>Платежей: {settings.payment_count || 0}</div>
            {settings.last_payment && <div>Последний: {new Date(settings.last_payment).toLocaleDateString()}</div>}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['manual', '🔧 Ручная настройка'], ['guided', '🚀 Быстрая настройка']].map(([id, label]) => (
          <div key={id} onClick={() => setTab(id)} style={{
            padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
            background: tab === id ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
            border: `1px solid ${tab === id ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.1)'}`,
          }}>{label}</div>
        ))}
      </div>

      {tab === 'guided' && (
        <div style={{ padding: 16, borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Пошаговая инструкция:</div>
          <div style={{ fontSize: 13, lineHeight: 1.8 }}>
            <div style={{ marginBottom: 8 }}>
              <strong>1.</strong> Откройте{' '}
              <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer"
                style={{ color: '#818cf8' }}>dashboard.stripe.com/apikeys</a>
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>2.</strong> Скопируйте <strong>Secret Key</strong> (sk_live_... или sk_test_...) и вставьте ниже
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>3.</strong> Скопируйте <strong>Publishable Key</strong> (pk_live_...) и вставьте ниже
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>4.</strong> Перейдите в{' '}
              <a href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer"
                style={{ color: '#818cf8' }}>Webhooks</a> → Add endpoint
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>5.</strong> URL вебхука:{' '}
              <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
                {settings?.webhook_url || 'https://api.workifypl.com/stripe/webhook/...'}
              </code>
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>6.</strong> Events: <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: 4, fontSize: 11 }}>
                checkout.session.completed, payment_intent.succeeded
              </code>
            </div>
            <div><strong>7.</strong> Скопируйте <strong>Webhook Secret</strong> (whsec_...) и вставьте ниже</div>
          </div>
        </div>
      )}

      {/* Key inputs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>Secret Key {settings?.stripe_secret_key && `(текущий: ${settings.stripe_secret_key})`}</label>
          <input type="password" placeholder="sk_live_... или sk_test_..." value={form.stripe_secret_key}
            onChange={e => setForm(f => ({ ...f, stripe_secret_key: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Publishable Key {settings?.stripe_publishable_key && `(текущий: ${settings.stripe_publishable_key})`}</label>
          <input type="password" placeholder="pk_live_..." value={form.stripe_publishable_key}
            onChange={e => setForm(f => ({ ...f, stripe_publishable_key: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>Webhook Secret {settings?.stripe_webhook_secret && `(текущий: ${settings.stripe_webhook_secret})`}</label>
          <input type="password" placeholder="whsec_..." value={form.stripe_webhook_secret}
            onChange={e => setForm(f => ({ ...f, stripe_webhook_secret: e.target.value }))} style={inputStyle} />
        </div>
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <button onClick={handleSave} disabled={saving} style={btnStyle()}>
          {saving ? '...' : '💾 Сохранить'}
        </button>
        <button onClick={handleTest} disabled={testing} style={btnStyle('#10b981')}>
          {testing ? '...' : '🔌 Тест подключения'}
        </button>
      </div>

      {msg && <div style={{ padding: 10, borderRadius: 8, background: 'rgba(255,255,255,0.05)', marginBottom: 12, fontSize: 13 }}>{msg}</div>}

      {testResult && (
        <div style={{
          padding: 12, borderRadius: 8, marginBottom: 16,
          background: testResult.ok ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
          border: `1px solid ${testResult.ok ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
          fontSize: 13,
        }}>
          {testResult.ok ? (
            <div>✅ Подключение успешно! Account: {testResult.account_id}{testResult.country && ` (${testResult.country})`}</div>
          ) : (
            <div>❌ {testResult.error}</div>
          )}
        </div>
      )}

      {/* Auto-invoice toggles */}
      {settings?.connected && (
        <div style={{ padding: 16, borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>⚙️ Автоматизация</div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 13 }}>Автоматически создавать фактуру</div>
              <div style={{ fontSize: 11, opacity: 0.5 }}>При получении платежа через Stripe</div>
            </div>
            <div onClick={() => {}} style={{
              width: 44, height: 24, borderRadius: 12, cursor: 'default',
              background: 'rgba(16,185,129,0.6)', position: 'relative',
            }}>
              <div style={{ width: 20, height: 20, borderRadius: 10, background: '#fff', position: 'absolute', top: 2, right: 2 }} />
            </div>
          </div>

          {settings.ksef_configured && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13 }}>Автоматически отправлять в KSeF</div>
                <div style={{ fontSize: 11, opacity: 0.5 }}>e-Faktura сразу после создания</div>
              </div>
              <div onClick={() => toggleAutoInvoice('ksef_auto_send', !settings.ksef_auto_send)} style={{
                width: 44, height: 24, borderRadius: 12, cursor: 'pointer',
                background: settings.ksef_auto_send ? 'rgba(16,185,129,0.6)' : 'rgba(255,255,255,0.1)',
                position: 'relative', transition: 'background 0.2s',
              }}>
                <div style={{
                  width: 20, height: 20, borderRadius: 10, background: '#fff', position: 'absolute', top: 2,
                  transition: 'left 0.2s, right 0.2s',
                  ...(settings.ksef_auto_send ? { right: 2 } : { left: 2 }),
                }} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
